import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, Loader2, Play } from 'lucide-react';
import { toast } from 'react-hot-toast';
import FormattedDate from '@/components/common/FormattedDate';
import { useDrainStatus, useDrainWorker, useUndrainWorker } from '@/hooks/useJobs';
import type { WorkerDrainStatusResponse, WorkerEnriched } from '@/lib/types';

interface WorkerInfo {
  worker_id: string;
  active_jobs: number;
  total_jobs: number;
  last_heartbeat: string;
  last_claim: string;
  status: 'active' | 'idle' | 'stale';
}

interface WorkersTableProps {
  workers: WorkerInfo[] | WorkerEnriched[];
  loading: boolean;
}

// Type guard to check if workers are enriched
function isEnrichedWorker(worker: WorkerInfo | WorkerEnriched): worker is WorkerEnriched {
  return 'cloud_name' in worker;
}

export default function WorkersTable({ workers, loading }: WorkersTableProps) {
  const navigate = useNavigate();

  const handleWorkerClick = useCallback(
    (workerId: string) => {
      navigate(`/jobs?worker_id=${encodeURIComponent(workerId)}`);
    },
    [navigate]
  );

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        <p className="mt-4 text-sm text-muted-foreground">Loading workers...</p>
      </div>
    );
  }

  if (workers.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No workers found</p>
      </div>
    );
  }

  // Check if we have enriched data
  const hasEnrichedData = workers.length > 0 && isEnrichedWorker(workers[0]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            {hasEnrichedData ? (
              <>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Worker ID</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Location</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">GPU</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Current Job</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Customer</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Last Heartbeat</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Drain</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
              </>
            ) : (
              <>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Worker ID</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Active Jobs</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Total Jobs</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Last Claim</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Last Heartbeat</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {workers.map((worker) =>
            isEnrichedWorker(worker) ? (
              <EnrichedWorkerRow key={worker.worker_id} worker={worker} onWorkerClick={handleWorkerClick} />
            ) : (
              <BasicWorkerRow key={worker.worker_id} worker={worker} onWorkerClick={handleWorkerClick} />
            )
          )}
        </tbody>
      </table>
    </div>
  );
}

// The drain-status query (observer + cache entry + polling) exists only for cordoned rows —
// the Workers page renders up to 500 rows and hooks can't be called conditionally in a map,
// so the split lives at the component boundary instead.
const EnrichedWorkerRow = memo(function EnrichedWorkerRow({
  worker,
  onWorkerClick,
}: {
  worker: WorkerEnriched;
  onWorkerClick: (workerId: string) => void;
}) {
  return worker.drain_requested_ts != null ? (
    <CordonedEnrichedRow worker={worker} onWorkerClick={onWorkerClick} />
  ) : (
    <EnrichedRowView worker={worker} onWorkerClick={onWorkerClick} drainStatus={undefined} drainStatusError={false} />
  );
});

// The single per-row query feeds both the badge ack state and the drain cell, so the two
// can never disagree ("unacked" badge next to a "Drained" cell) across snapshot boundaries.
function CordonedEnrichedRow({
  worker,
  onWorkerClick,
}: {
  worker: WorkerEnriched;
  onWorkerClick: (workerId: string) => void;
}) {
  const drainQuery = useDrainStatus(worker.worker_id, worker.drain_requested_ts);
  return (
    <EnrichedRowView
      worker={worker}
      onWorkerClick={onWorkerClick}
      drainStatus={drainQuery.data}
      drainStatusError={drainQuery.isError}
    />
  );
}

function EnrichedRowView({
  worker,
  onWorkerClick,
  drainStatus,
  drainStatusError,
}: {
  worker: WorkerEnriched;
  onWorkerClick: (workerId: string) => void;
  // object = current-epoch server truth; null = the server answered for a DIFFERENT cordon
  // epoch (this row's list snapshot is stale); undefined = no drain-status response (yet)
  drainStatus: WorkerDrainStatusResponse | null | undefined;
  drainStatusError: boolean;
}) {
  const cordoned = worker.drain_requested_ts != null;
  // Ack shown from the freshest source: the current-epoch drain-status response when we have
  // one; the list snapshot before the first response lands. On an epoch mismatch claim the
  // safe "unacked" state — the list row's ack belongs to a cordon that no longer exists.
  const acked = drainStatus
    ? drainStatus.drain_ack_ts != null
    : drainStatus === null
      ? false
      : worker.drain_ack_ts != null;

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
      <WorkerIdCell workerId={worker.worker_id} onWorkerClick={onWorkerClick} />
      <td className="p-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          <WorkerStatusBadge status={worker.status} />
          {cordoned && <CordonedBadge acked={acked} reason={worker.drain_reason} />}
        </div>
      </td>
      <td className="p-4">
        <div className="text-sm">
          <div className="font-medium">{worker.cloud_name}</div>
          <div className="text-xs text-muted-foreground">
            {worker.region_name} / {worker.zone_name}
          </div>
        </div>
      </td>
      <td className="p-4">
        {worker.gpu_model ? (
          <div className="text-sm">
            <div className="font-medium">{worker.gpu_model}</div>
            <div className="text-xs text-muted-foreground">
              {worker.vram_gb != null ? `${worker.vram_gb}GB VRAM` : 'VRAM unknown'}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">CPU only</span>
        )}
      </td>
      <td className="p-4">
        {worker.current_job ? (
          <div className="text-sm">
            <div className="font-mono text-xs">{worker.current_job.id}</div>
            <div className="text-xs text-muted-foreground">{worker.current_job.type_name}</div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-4">
        {worker.customer_name ? (
          <span className="text-sm">{worker.customer_name}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-4">
        <span className="text-sm text-muted-foreground">
          <FormattedDate date={worker.last_heartbeat_ts} />
        </span>
      </td>
      <WorkerDrainStatusCell cordoned={cordoned} drainStatus={drainStatus ?? null} isError={drainStatusError} />
      <td className="p-4 text-right">
        <WorkerActions workerId={worker.worker_id} cordoned={cordoned} />
      </td>
    </tr>
  );
}

const BasicWorkerRow = memo(function BasicWorkerRow({
  worker,
  onWorkerClick,
}: {
  worker: WorkerInfo;
  onWorkerClick: (workerId: string) => void;
}) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
      <WorkerIdCell workerId={worker.worker_id} onWorkerClick={onWorkerClick} />
      <td className="p-4">
        <WorkerStatusBadge status={worker.status} />
      </td>
      <td className="p-4 text-right">
        <span className="text-sm">{worker.active_jobs}</span>
      </td>
      <td className="p-4 text-right">
        <span className="text-sm">{worker.total_jobs}</span>
      </td>
      <td className="p-4">
        <span className="text-sm text-muted-foreground">
          <FormattedDate date={worker.last_claim} />
        </span>
      </td>
      <td className="p-4">
        <span className="text-sm text-muted-foreground">
          <FormattedDate date={worker.last_heartbeat} />
        </span>
      </td>
    </tr>
  );
});

function WorkerIdCell({ workerId, onWorkerClick }: { workerId: string; onWorkerClick: (workerId: string) => void }) {
  return (
    <td className="p-4">
      <button
        type="button"
        onClick={() => onWorkerClick(workerId)}
        className="font-mono text-sm text-primary hover:underline cursor-pointer"
      >
        {workerId}
      </button>
    </td>
  );
}

interface WorkerStatusBadgeProps {
  status: 'active' | 'idle' | 'stale' | 'processing' | 'inactive';
}

function WorkerStatusBadge({ status }: WorkerStatusBadgeProps) {
  // Map API statuses to display statuses
  const displayStatus = status === 'processing' ? 'active' : status === 'inactive' ? 'stale' : status;

  const statusConfig: Record<'active' | 'idle' | 'stale', { label: string; className: string; pulseClassName?: string }> = {
    active: {
      label: 'Processing',
      className: 'bg-green-500/10 text-green-500 border-green-500/30',
      pulseClassName: 'animate-pulse',
    },
    idle: {
      label: 'Idle',
      className: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    },
    stale: {
      label: 'Inactive',
      className: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
    },
  };

  const config = statusConfig[displayStatus];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}
    >
      {displayStatus === 'active' && config.pulseClassName && <span className={`h-1.5 w-1.5 rounded-full bg-green-500 ${config.pulseClassName}`} />}
      {config.label}
    </span>
  );
}

function CordonedBadge({ acked, reason }: { acked: boolean; reason: string | null }) {
  const state = acked
    ? 'Cordoned — the worker acknowledged the cordon and stops claiming new jobs'
    : 'Cordon requested, not acknowledged by the worker yet — not enforced for pull claims';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border bg-amber-500/10 text-amber-500 border-amber-500/30"
      title={reason ? `${state}. Reason: ${reason}` : state}
    >
      {acked ? 'Cordoned' : 'Cordoned · unacked'}
    </span>
  );
}

function WorkerDrainStatusCell({
  cordoned,
  drainStatus,
  isError,
}: {
  cordoned: boolean;
  drainStatus: WorkerDrainStatusResponse | null;
  isError: boolean;
}) {
  let content;
  if (!cordoned) {
    content = <span className="text-sm text-muted-foreground">—</span>;
  } else if (drainStatus) {
    // Last-known data beats a transient refetch failure — polling stays on the fast cadence
    // while errored, so a "(stale)" marker self-clears on the next successful poll
    const staleHint = isError ? <span className="text-xs text-red-400"> (stale)</span> : null;
    if (drainStatus.is_drained) {
      content = (
        <span className="text-sm font-medium text-green-500">
          Drained
          {staleHint}
        </span>
      );
    } else {
      // Drained requires BOTH planes at zero — show the larger count so a blocking plane is never masked
      const activeCount = Math.max(drainStatus.active_job_count, drainStatus.active_assignments_count ?? 0);
      content = (
        <div
          className="text-sm"
          title={`Registry jobs: ${drainStatus.active_job_count} · unreleased assignments: ${
            drainStatus.active_assignments_count ?? 'unavailable'
          }`}
        >
          <span className="font-medium text-amber-500">
            Draining
            {staleHint}
          </span>
          <div className="text-xs text-muted-foreground">
            {activeCount} active{drainStatus.jobs_stats_available ? '' : ' (registry only)'}
          </div>
        </div>
      );
    }
  } else if (isError) {
    content = <span className="text-sm text-red-400">status unavailable</span>;
  } else {
    content = <span className="text-sm text-muted-foreground">checking…</span>;
  }

  return <td className="p-4">{content}</td>;
}

function WorkerActions({ workerId, cordoned }: { workerId: string; cordoned: boolean }) {
  const drainWorker = useDrainWorker();
  const undrainWorker = useUndrainWorker();
  const pending = drainWorker.isPending || undrainWorker.isPending;
  const verb = cordoned ? 'Uncordon' : 'Cordon';

  const handleToggle = async () => {
    if (pending) {
      return;
    }
    const detail = cordoned
      ? 'It will resume claiming jobs.'
      : 'It will stop claiming new jobs; running jobs finish.';
    if (!confirm(`${verb} worker "${workerId}"? ${detail}`)) {
      return;
    }
    try {
      await (cordoned ? undrainWorker : drainWorker).mutateAsync(workerId);
      toast.success(`Worker ${workerId} ${cordoned ? 'uncordoned' : 'cordoned'}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${verb.toLowerCase()} worker`);
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      // aria-disabled + click guard instead of native disabled: a disabled element is
      // unfocusable, so the browser would eject keyboard focus mid-interaction — and on the
      // failure path (toast, no row remount) it would never come back
      aria-disabled={pending}
      aria-label={`${verb} worker ${workerId}`}
      aria-busy={pending}
      className={`p-1.5 rounded-md transition-colors ${
        pending
          ? 'opacity-50 cursor-not-allowed'
          : cordoned
            ? 'hover:bg-green-500/20'
            : 'hover:bg-amber-500/20'
      } ${cordoned ? 'text-green-500' : 'text-amber-500'}`}
      title={cordoned ? 'Uncordon — resume claiming jobs' : 'Cordon — stop claiming new jobs'}
    >
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : cordoned ? (
        <Play className="h-4 w-4" />
      ) : (
        <Ban className="h-4 w-4" />
      )}
    </button>
  );
}
